import { useEffect, useState, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { BsCloudUpload } from "react-icons/bs";
import { FaArrowLeft } from "react-icons/fa";
import { GrFormAdd } from "react-icons/gr";
import { MdDeleteForever } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { useSession } from "../../../../context/SessionContext";
import { getallVendors } from "../../../../Redux/actions/Operationsactions/getvendors";
import { getallMaterial } from "../../../../Redux/actions/Operationsactions/getmaterial";
import { getPopulateUsers } from "../../../../Redux/actions/accountsactions/getContractComments";
import { getallPurchaseOrders } from "../../../../Redux/actions/Operationsactions/getpurchaseorders";
import {
  AddPurchaseOrderApi,
  UpdatePurchaseOrderApi,
} from "../../../../Api/Operations/PurchaseOrders";
import ActionButton from "../../../Common/ActionButton";
import { InputField, SelectField, TextAreaField } from "../../../Common/FormComponents";
import { filterStoreProjects } from "../../projectFilters";
import "./AddPurchaseOrder.css";
import "../../AddContractorsAndVendors.css";

const headerCellStyle = {
  height: "42px",
  paddingTop: "6px",
  paddingBottom: "6px",
  verticalAlign: "middle",
  lineHeight: "1.1",
};

// PO quantities are stored to 2 dp (numeric(18,2)).
const floor2 = (n) => Math.floor(n * 100 + 1e-6) / 100;
const round2 = (n) => Math.round(n * 100) / 100;
const EMPTY_LINE = {
  MaterialId: "",
  Specification: "",
  MaterialPackagingId: "",
  Quantity: 0,
  UnitPrice: 0,
};

// One PO line per PR line handed over by the Purchase Requisition screen.
const linesFromRequisitions = (prs) =>
  prs.flatMap((pr) =>
    (pr.lines || []).map((l) => ({
      ...EMPTY_LINE,
      MaterialId: l.materialId,
      MaterialName: l.materialName,
      Quantity: floor2(Number(l.openQuantity) || 0),
      UnitPrice: Number(l.estimatedRate) || 0,
      PurchaseRequisitionLineId: l.lineId,
      PrNumber: pr.prNumber,
      OpenBase: Number(l.openQuantity) || 0,
      EstimatedRate: Number(l.estimatedRate) || 0,
      BaseUomName: l.baseUomName,
      Packagings: l.packagings || [],
    })),
  );

// Lines of an existing PO (GetById shape) keep their PR link.
const linesFromPo = (p) =>
  p.Lines.map((l) => ({
    MaterialId: l.MaterialId || "",
    MaterialName: l.MaterialName,
    Specification: l.Specification || l.Brand || "",
    MaterialPackagingId: l.MaterialPackagingId || "",
    Quantity: Number(l.Quantity) || 0,
    UnitPrice: Number(l.UnitPrice) || 0,
    PurchaseRequisitionLineId: l.PurchaseRequisitionLineId || undefined,
    PrNumber: l.PrNumber,
  }));

const buildLines = (p) => {
  if (p.fromPurchaseRequisitions?.length > 0) {
    return linesFromRequisitions(p.fromPurchaseRequisitions);
  }
  return (p.Lines || []).length > 0 ? linesFromPo(p) : null;
};

/**
 * Purchase Orders Form Component
 */
const AddPurchaseOrders = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const vendorsData = useSelector((state) => state.getAllVendors?.data || []);
  const materialData = useSelector((state) => state.getAllMaterial?.data || []);
  const populateUsers = useSelector((s) => s.getAllPopulateUsers?.data || []);

  const { session, rolesdata } = useSession();
  const projects = filterStoreProjects(rolesdata?.data?.Projects) || [];
  const populatedata = location.state || {};
  const fromPrs = populatedata.fromPurchaseRequisitions?.length > 0;
  const prProjectId = fromPrs ? populatedata.fromPurchaseRequisitions[0].projectId : "";

  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [vendors, setVendors] = useState([]);

  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  console.log("filePreview", filePreview);
  const [approverUserIds, setApproverUserIds] = useState(
    populatedata.Approvals?.map((approval) => approval.ApproverUserId) ||
      (populatedata.ApproverUserId ? [populatedata.ApproverUserId] : []),
  );

  const [formData, setFormData] = useState({
    ProjectId:
      prProjectId ||
      populatedata.ProjectId ||
      session?.ProjectId ||
      (projects.length > 0 ? projects[0].ProjectId : ""),
    VendorId: populatedata.VendorId || "",
    PoDate: populatedata.PoDate
      ? moment(populatedata.PoDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD"),
    PaymentTerms: populatedata.PaymentTerms || "",
    DeliveryDate: populatedata.DeliveryDate
      ? moment(populatedata.DeliveryDate).format("YYYY-MM-DD")
      : moment().add(5, "days").format("YYYY-MM-DD"),
    DeliverySite: populatedata.DeliverySite || populatedata.ReceivingSite || "",
    Description: populatedata.Description || populatedata.Notes || "",
    FreightAmount: populatedata.FreightAmount ?? populatedata.Freight ?? 0,
    TaxAmount: populatedata.TaxAmount ?? 0,
  });

  const [lines, setLines] = useState(buildLines(populatedata) || [{ ...EMPTY_LINE }]);

  useEffect(() => {
    dispatch(getallVendors(formData.ProjectId || undefined));
    dispatch(getallMaterial());
    if (formData.ProjectId) {
      dispatch(getPopulateUsers(formData.ProjectId));
    } else {
      dispatch(getPopulateUsers());
    }
  }, [dispatch, formData.ProjectId]);

  useEffect(() => {
    if (vendorsData && vendorsData.length > 0) {
      setVendors(vendorsData);
    }
  }, [vendorsData]);

  // Session is read through a ref so a late session update does not rebuild (and wipe) the form.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  useEffect(() => {
    if (location.state) {
      const p = location.state;
      setFormData({
        ProjectId:
          p.fromPurchaseRequisitions?.[0]?.projectId ||
          p.ProjectId ||
          sessionRef.current?.ProjectId ||
          "",
        VendorId: p.VendorId || "",
        PoDate: p.PoDate ? moment(p.PoDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        PaymentTerms: p.PaymentTerms || "",
        DeliveryDate: p.DeliveryDate
          ? moment(p.DeliveryDate).format("YYYY-MM-DD")
          : moment().add(5, "days").format("YYYY-MM-DD"),
        DeliverySite: p.DeliverySite || p.ReceivingSite || "",
        Description: p.Description || p.Notes || "",
        FreightAmount: p.FreightAmount ?? p.Freight ?? 0,
        TaxAmount: p.TaxAmount ?? 0,
      });

      const built = buildLines(p);
      if (built) {
        setLines(built);
      }
    }
  }, [location.state]);

  // Dropdown options
  const vendorOptions = useMemo(() => {
    const base = vendors.map((v) => ({
      value: v.Id,
      label: v.Name || v.VendorName,
    }));
    return [{ value: "", label: "Select Vendor" }, ...base];
  }, [vendors]);

  const paymentTermsOptions = [
    { value: "", label: "Select Payment Terms" },
    { value: "Advance (100%)", label: "Advance (100%)" },
    { value: "Cash on Delivery (COD)", label: "Cash on Delivery (COD)" },
    { value: "Credit - 7 Days", label: "Credit - 7 Days" },
    { value: "Credit - 15 Days", label: "Credit - 15 Days" },
    { value: "Credit - 30 Days", label: "Credit - 30 Days" },
    { value: "Running Bill / Progress Payment", label: "Running Bill / Progress Payment" },
  ];

  const userOptions = useMemo(() => {
    const allUsers = [
      ...populateUsers,
      ...(session?.LoginId
        ? [{ Id: session.LoginId, FullName: session.UserName || "Current User" }]
        : []),
    ];
    const userMap = new Map();
    allUsers.forEach((u) => {
      if (u?.Id && !userMap.has(u.Id)) {
        userMap.set(u.Id, u.FullName || u.Name || u.Email || u.Id);
      }
    });

    const list = Array.from(userMap.entries()).map(([value, label]) => ({
      value,
      label,
    }));

    return [{ value: "", label: "Select Approver" }, ...list];
  }, [populateUsers, session]);

  const handleChange = (e) => {
    const { id, name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name || id]: value,
    }));
  };

  // Line Items Handlers
  function addLineRow() {
    setLines([...lines, { ...EMPTY_LINE, Quantity: 1 }]);
  }

  function removeLineRow(index) {
    if (lines.length <= 1) {
      toast.info("At least one line item is required");
      return;
    }
    setLines(lines.filter((_, i) => i !== index));
  }

  function updateLine(index, field, value) {
    const updatedLines = [...lines];
    updatedLines[index][field] = value;
    setLines(updatedLines);
  }

  // Packagings a line can use: the PR line's own list, else the material's.
  function packagingsOf(line) {
    return (
      line.Packagings ||
      materialData.find((m) => String(m.Id) === String(line.MaterialId))?.Packagings ||
      []
    );
  }

  function conversionOf(line) {
    if (!line.MaterialPackagingId) {
      return 1;
    }
    const pkg = packagingsOf(line).find((p) => String(p.Id) === String(line.MaterialPackagingId));
    return Number(pkg?.ConversionToBase) || 1;
  }

  // Largest quantity (in the chosen unit) a PR line may take; null for lines without a known open qty.
  function maxQtyOf(line) {
    return line.OpenBase == null ? null : floor2(line.OpenBase / conversionOf(line));
  }

  function updatePackaging(index, packagingId) {
    const updatedLines = [...lines];
    const line = { ...updatedLines[index], MaterialPackagingId: packagingId };
    if (line.OpenBase != null) {
      const conv = conversionOf(line);
      line.Quantity = floor2(line.OpenBase / conv);
      line.UnitPrice = Number((line.EstimatedRate * conv).toFixed(4));
    }
    updatedLines[index] = line;
    setLines(updatedLines);
  }

  function updateLineMaterial(index, materialId) {
    const updatedLines = [...lines];
    const mat = materialData.find((m) => String(m.Id) === String(materialId));
    updatedLines[index].MaterialId = materialId;
    updatedLines[index].MaterialPackagingId = "";
    if (mat?.Brand) {
      updatedLines[index].Specification = mat.Brand;
    }
    setLines(updatedLines);
  }

  // Calculations
  const subtotal = useMemo(() => {
    return lines.reduce(
      (sum, line) => sum + (Number(line.Quantity) || 0) * (Number(line.UnitPrice) || 0),
      0,
    );
  }, [lines]);

  const grandTotal = useMemo(() => {
    return subtotal + (Number(formData.FreightAmount) || 0) + (Number(formData.TaxAmount) || 0);
  }, [subtotal, formData.FreightAmount, formData.TaxAmount]);

  function handleFileChange(e) {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (selectedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  }

  // Submit Handler
  async function addPurchaseOrder(e) {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const {
      ProjectId,
      VendorId,
      PoDate,
      DeliveryDate,
      PaymentTerms,
      DeliverySite,
      Description,
      FreightAmount,
      TaxAmount,
    } = formData;

    if (!VendorId) {
      toast.info("Please select a Vendor");
      return;
    }

    if (!PoDate) {
      toast.info("PO Date is required");
      return;
    }

    if (lines.length === 0) {
      toast.info("Please add at least one line item");
      return;
    }

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.MaterialId) {
        toast.info(`Material is required for line item ${i + 1}`);
        return;
      }
      if (!line.Quantity || parseFloat(line.Quantity) <= 0) {
        toast.info(`Valid Quantity is required for line item ${i + 1}`);
        return;
      }
      if (!line.UnitPrice || parseFloat(line.UnitPrice) <= 0) {
        toast.info(`Valid Unit Price is required for line item ${i + 1}`);
        return;
      }
      if (
        line.OpenBase != null &&
        Number(line.Quantity) * conversionOf(line) > line.OpenBase + 0.005
      ) {
        toast.info(
          `Line item ${i + 1} (${line.PrNumber}) exceeds the open requisition quantity of ${line.OpenBase} ${line.BaseUomName || ""}`,
        );
        return;
      }
    }

    const data = new FormData();
    data.append("PurchaseOrderId", populatedata.Id || 0);
    data.append("VendorId", VendorId);
    data.append("PoDate", PoDate);
    data.append("DeliveryDate", DeliveryDate || "");
    data.append("PaymentTerms", PaymentTerms || "");
    data.append("DeliverySite", DeliverySite || "");
    data.append("Description", Description || "");
    data.append("FreightAmount", String(FreightAmount || 0));
    data.append("Freight", String(FreightAmount || 0));
    data.append("TaxAmount", String(TaxAmount || 0));
    data.append("Subtotal", String(subtotal));
    data.append("TotalAmount", String(grandTotal));
    data.append("ProjectId", ProjectId || session?.ProjectId || projects[0]?.ProjectId || "1");

    // Add lines array
    lines.forEach((line, index) => {
      data.append(`Lines[${index}].MaterialId`, line.MaterialId || "");
      data.append(`Lines[${index}].Specification`, line.Specification || "");
      data.append(`Lines[${index}].Brand`, line.Specification || "");
      // Ordering the whole remaining PR quantity: send it in base units so a pack size that
      // doesn't divide the open quantity still fills the requisition line exactly.
      const conv = conversionOf(line);
      const full = line.OpenBase != null && Number(line.Quantity) >= maxQtyOf(line);
      if (line.MaterialPackagingId && !full) {
        data.append(`Lines[${index}].MaterialPackagingId`, line.MaterialPackagingId);
      }
      if (line.PurchaseRequisitionLineId) {
        data.append(`Lines[${index}].PurchaseRequisitionLineId`, line.PurchaseRequisitionLineId);
      }
      data.append(
        `Lines[${index}].Quantity`,
        String(full ? round2(line.OpenBase) : line.Quantity || 0),
      );
      data.append(
        `Lines[${index}].UnitPrice`,
        String(
          full ? Number(((Number(line.UnitPrice) || 0) / conv).toFixed(4)) : line.UnitPrice || 0,
        ),
      );
    });

    // Add approver user IDs
    if (approverUserIds?.length > 0) {
      approverUserIds.forEach((id, index) => {
        data.append(`ApproverUserIds[${index}]`, id);
      });
    }

    // Add file
    if (file) {
      data.append("File", file);
    } else if (populatedata.File) {
      data.append("File", populatedata.File);
    }

    setIsLoading(true);
    try {
      let result;
      if (populatedata.Id) {
        result = await UpdatePurchaseOrderApi(data, populatedata.Id);
      } else {
        result = await AddPurchaseOrderApi(data);
      }

      if (result?.data?.IsSuccess || result?.status === 200) {
        toast.success(result?.data?.Message || "Purchase Order saved successfully");
        dispatch(getallPurchaseOrders(ProjectId));
        history.goBack();
      } else {
        toast.error(result?.data?.Message || "Failed to save purchase order");
      }
    } catch (error) {
      toast.error("Error saving purchase order");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="purchase-order-screen-wrapper">
      {/* Top Header */}
      <div className="col-md-12 text-left page_heading">
        <div className="d-flex align-items-center">
          <FaArrowLeft
            size={16}
            style={{ cursor: "pointer", marginRight: "12px" }}
            title="Back"
            onClick={() => history.goBack()}
          />
          <h2 className="grn-header-title">{populatedata.Id ? "Edit" : "Add"} Purchase Order</h2>
        </div>
      </div>

      <form onSubmit={addPurchaseOrder} autoComplete="off">
        {/* CARD 1: PO Header Information */}
        <div className="po-card">
          <h5 className="po-card-title">Purchase Order Details</h5>

          {/* Row 1 */}
          <div className="row mb-2">
            <SelectField
              label="Vendor:"
              name="VendorId"
              value={formData.VendorId}
              onChange={handleChange}
              options={vendorOptions}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <SelectField
              label="Payment Terms:"
              name="PaymentTerms"
              value={formData.PaymentTerms}
              onChange={handleChange}
              options={paymentTermsOptions}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <InputField
              label="PO Date:"
              name="PoDate"
              type="date"
              value={formData.PoDate}
              onChange={handleChange}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <InputField
              label="Delivery Date:"
              name="DeliveryDate"
              type="date"
              value={formData.DeliveryDate}
              onChange={handleChange}
              required
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            <InputField
              label="Delivery Site / Gate:"
              name="DeliverySite"
              value={formData.DeliverySite}
              onChange={handleChange}
              placeholder="e.g. Main gate / site store"
              stacked
              wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
            />
            {(projects.length > 1 || fromPrs) && (
              <SelectField
                label="Project:"
                name="ProjectId"
                value={formData.ProjectId}
                onChange={handleChange}
                options={[
                  { value: "", label: "Select Project" },
                  ...projects.map((p) => ({
                    value: p.ProjectId,
                    label: p.ProjectName,
                  })),
                  // A PR's project may be outside the user's role-filtered list; show it anyway.
                  ...(formData.ProjectId &&
                  !projects.some((p) => String(p.ProjectId) === String(formData.ProjectId))
                    ? [
                        {
                          value: formData.ProjectId,
                          label:
                            populatedata.fromPurchaseRequisitions?.[0]?.projectName ||
                            populatedata.ProjectName ||
                            String(formData.ProjectId),
                        },
                      ]
                    : []),
                ]}
                required
                disabled={fromPrs || lines.some((l) => l.PurchaseRequisitionLineId)}
                stacked
                wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
              />
            )}
          </div>

          {/* Optional Project row if multiple projects exist */}
        </div>

        {/* CARD 2: Line Items Table (DemandNote Style) */}
        <div className="po-card">
          <h5 className="po-card-title">Line Items</h5>

          <div className="col-md-12 p-0 column">
            <div className="table-responsive">
              <table
                className="table table-bordered table-hover"
                id="tab_logic"
                style={{ fontSize: "13px" }}
              >
                <thead style={{ backgroundColor: "#E8FBFB" }}>
                  <tr style={{ height: "42px" }}>
                    <th className="text-center" style={{ ...headerCellStyle, width: 50 }}>
                      Sr#
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "180px" }}>
                      Material <span className="text-danger">*</span>
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "160px" }}>
                      Specification / Brand
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, minWidth: "140px" }}>
                      Packaging/Unit
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: "110px" }}>
                      Qty <span className="text-danger">*</span>
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: "130px" }}>
                      Unit Price (PKR) <span className="text-danger">*</span>
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: "130px" }}>
                      Total (PKR)
                    </th>
                    <th className="text-center" style={{ ...headerCellStyle, width: 50 }}>
                      <span onClick={addLineRow} style={{ cursor: "pointer" }} title="Add Row">
                        <GrFormAdd className="add_icon drow" size="1.2em" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody id="tablebody">
                  {lines.map((line, idx) => {
                    const selectedMat = materialData.find(
                      (m) => String(m.Id) === String(line.MaterialId),
                    );
                    const packagingList = packagingsOf(line);
                    const maxQty = maxQtyOf(line);
                    const conv = conversionOf(line);
                    const baseName = line.BaseUomName || selectedMat?.UomName;

                    return (
                      <tr key={idx} id={`addr${idx}`}>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          {idx + 1}
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          {line.PurchaseRequisitionLineId ? (
                            <div>
                              <div>{line.MaterialName || selectedMat?.ItemName}</div>
                              {line.PrNumber && (
                                <small className="text-muted">PR: {line.PrNumber}</small>
                              )}
                            </div>
                          ) : (
                            <select
                              name="MaterialId"
                              value={line.MaterialId || ""}
                              onChange={(e) => updateLineMaterial(idx, e.target.value)}
                              className="input_styling"
                              style={{ width: "100%", margin: 0 }}
                              required
                            >
                              <option value="">Select Material</option>
                              {materialData.map((m) => (
                                <option key={m.Id} value={m.Id}>
                                  {m.ItemName}
                                </option>
                              ))}
                              {materialData.length === 0 && (
                                <>
                                  <option value="mat-1">OPC Cement</option>
                                  <option value="mat-2">Deformed Bar 1/2"</option>
                                </>
                              )}
                            </select>
                          )}
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <input
                            type="text"
                            name="Specification"
                            value={line.Specification || ""}
                            onChange={(e) => updateLine(idx, "Specification", e.target.value)}
                            className="input_styling"
                            placeholder="e.g. brand or grade"
                            style={{ width: "100%", margin: 0 }}
                          />
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <select
                            name="MaterialPackagingId"
                            value={line.MaterialPackagingId || ""}
                            onChange={(e) => updatePackaging(idx, e.target.value)}
                            className="input_styling"
                            style={{ width: "100%", margin: 0 }}
                          >
                            <option value="">
                              {baseName
                                ? baseName
                                : idx === 0
                                  ? "Bags"
                                  : idx === 1
                                    ? "Metric Ton"
                                    : "Base Unit"}
                            </option>
                            {packagingList.map((p) => (
                              <option key={p.Id} value={p.Id}>
                                {p.PackagingName} ({p.UomName || ""})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <input
                            type="number"
                            name="Quantity"
                            value={line.Quantity || ""}
                            onChange={(e) =>
                              updateLine(
                                idx,
                                "Quantity",
                                e.target.value === "" ? "" : Number(e.target.value),
                              )
                            }
                            className="input_styling text-center"
                            placeholder="0"
                            min="0"
                            max={maxQty ?? undefined}
                            step="any"
                            style={{ width: "100%", margin: 0 }}
                            required
                          />
                          {maxQty != null && (
                            <small className="text-muted d-block">Max {maxQty}</small>
                          )}
                          {line.OpenBase != null && conv !== 1 && (
                            <small className="text-muted d-block">
                              = {Number((Number(line.Quantity) * conv).toFixed(4))} {baseName}
                            </small>
                          )}
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <input
                            type="number"
                            name="UnitPrice"
                            value={line.UnitPrice || ""}
                            onChange={(e) =>
                              updateLine(
                                idx,
                                "UnitPrice",
                                e.target.value === "" ? "" : Number(e.target.value),
                              )
                            }
                            className="input_styling text-right"
                            placeholder="0"
                            min="0"
                            step="any"
                            style={{ width: "100%", margin: 0 }}
                            required
                          />
                        </td>
                        <td
                          className="text-center"
                          style={{
                            verticalAlign: "middle",
                            fontWeight: 600,
                            color: "#1e293b",
                          }}
                        >
                          Rs.{" "}
                          {(
                            (Number(line.Quantity) || 0) * (Number(line.UnitPrice) || 0)
                          ).toLocaleString()}
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <span onClick={() => removeLineRow(idx)}>
                            <MdDeleteForever
                              size="1.3em"
                              style={{ cursor: "pointer", color: "#dc3545" }}
                              title="Remove"
                            />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CARD 3: Summary & Commercial Terms */}
        <div className="po-card">
          <h5 className="po-card-title">Summary & Commercial Terms</h5>

          <div className="row">
            {/* Left side: Notes, Attachment, Approver */}
            <div className="col-lg-7 col-md-12">
              <div className="row align-items-start">
                <TextAreaField
                  label="Special Instructions / Notes:"
                  name="Description"
                  value={formData.Description}
                  onChange={handleChange}
                  placeholder="Rate includes unloading at site. Weighbridge slip required with delivery challan."
                  rows={3}
                  stacked
                  wrapperCol="col-lg-6 col-md-12 col-12"
                />
                <div className="col-lg-6 col-md-12 col-12 stacked-form-field ">
                  <label className="input_label d-block m-0 mb-2">
                    Attachment (Quotation/Bilty):
                  </label>
                  <div
                    className="document-upload-box"
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    <BsCloudUpload className="document-upload-icon" />
                    {file || populatedata.File ? (
                      <div className="document-upload-file">
                        <span
                          title={file?.name || populatedata.FileName || "Existing file attached"}
                        >
                          {file?.name || populatedata.FileName || "Existing file attached"}
                        </span>
                        {file && (
                          <button
                            type="button"
                            className="document-upload-remove"
                            onClick={(event) => {
                              event.stopPropagation();
                              setFile(null);
                              setFilePreview(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                            title="Remove attachment"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="document-upload-copy">
                        <strong>Click to upload</strong>
                        <span>Quotation, bilty or document</span>
                      </div>
                    )}
                    <input
                      id="purchase-order-file"
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="d-none"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                  </div>
                </div>
              </div>

              {/* Approver User */}
              <div className="row mb-2">
                <SelectField
                  label="Approver User:"
                  name="ApproverUserId"
                  value={approverUserIds[0] || ""}
                  onChange={(e) => setApproverUserIds(e.target.value ? [e.target.value] : [])}
                  options={userOptions}
                  labelCol="col-md-4"
                  inputCol="col-md-8"
                  required
                />
              </div>
            </div>

            {/* Right side: Financial Summary Box */}
            <div className="col-lg-5 col-md-12 mt-3 mt-lg-0">
              <div className="po-summary-box">
                {/* Subtotal */}
                <div className="po-summary-row">
                  <span className="po-summary-label">Subtotal:</span>
                  <span className="po-summary-val">Rs. {subtotal.toLocaleString()}</span>
                </div>

                {/* Freight */}
                <div className="po-summary-row">
                  <span className="po-summary-label">Freight / Carriage (PKR):</span>
                  <input
                    type="number"
                    className="po-summary-input"
                    name="FreightAmount"
                    value={formData.FreightAmount}
                    onChange={handleChange}
                  />
                </div>

                {/* Sales Tax / GST */}
                <div className="po-summary-row">
                  <span className="po-summary-label">Sales Tax / GST (PKR):</span>
                  <input
                    type="number"
                    className="po-summary-input"
                    name="TaxAmount"
                    value={formData.TaxAmount}
                    onChange={handleChange}
                  />
                </div>

                {/* Grand Total */}
                <div className="po-grand-total-row">
                  <span className="po-grand-total-label">Grand Total:</span>
                  <span className="po-grand-total-val">Rs. {grandTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="col-12 p-0">
          <div className="float-right mt-2 mb-4">
            <ActionButton
              text="Cancel"
              variant="cancel"
              type="button"
              onClick={() => history.goBack()}
              disabled={isLoading}
            />
            <ActionButton
              text="Save Purchase Order"
              icon={BsCloudUpload}
              type="submit"
              onClick={addPurchaseOrder}
              disabled={isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddPurchaseOrders;
